import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getMessageMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import {
  commonHeadersMPWeb,
  supportApiURL,
} from 'src/supportPortal/services/services'
import { getCountryCode, getMainLanguageCode, platformWeb } from 'src/consumer/services'

dayjs.extend(utc)

const VC_AUDIT_SESSIONS_API = '/api/secure/cc/support/vc-audit/sessions'

function buildHeaders() {
  const token = localStorage.getItem('token')
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()

  return {
    token,
    countryCode,
    mainLanguageCode,
    headers: {
      Authorization: `Bearer ${token}`,
      ...commonHeadersMPWeb,
      'ef-user-country-code': countryCode,
      'ef-app-language': mainLanguageCode,
    },
  }
}

function buildErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  )
}

function buildPageFallback(page = 0, size = 20) {
  return {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size,
    number: page,
  }
}

function normalizeArrayParam(value) {
  if (!value) {
    return undefined
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }
  return [value].filter(Boolean)
}

function normalizeTimestamp(value) {
  if (value === undefined || value === null || value === '') {
    return value
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return value
    }
    const millis = value < 100000000000 ? value * 1000 : value
    const parsed = dayjs.utc(millis)
    return parsed.isValid() ? parsed.toISOString() : value
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    if (!trimmedValue) {
      return value
    }
    if (/^\d+(\.\d+)?$/.test(trimmedValue)) {
      return normalizeTimestamp(Number(trimmedValue))
    }

    const utcParsed = dayjs.utc(trimmedValue)
    if (utcParsed.isValid()) {
      return utcParsed.toISOString()
    }

    const fallbackParsed = dayjs(trimmedValue)
    return fallbackParsed.isValid() ? fallbackParsed.toISOString() : value
  }

  if (Array.isArray(value)) {
    const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0, nano = 0] = value.map(Number)
    if ([year, month, day, hour, minute, second, nano].some(Number.isNaN)) {
      return value
    }
    return dayjs
      .utc(Date.UTC(year, Math.max(month - 1, 0), day, hour, minute, second, Math.floor(nano / 1000000)))
      .toISOString()
  }

  if (typeof value === 'object') {
    if (value.$date) {
      return normalizeTimestamp(value.$date)
    }
    if (typeof value.epochSecond === 'number') {
      return normalizeTimestamp((value.epochSecond * 1000) + Math.floor((value.nano || 0) / 1000000))
    }
    if (typeof value.seconds === 'number') {
      return normalizeTimestamp((value.seconds * 1000) + Math.floor((value.nanos || 0) / 1000000))
    }
    if (typeof value._seconds === 'number') {
      return normalizeTimestamp((value._seconds * 1000) + Math.floor((value._nanoseconds || 0) / 1000000))
    }
  }

  return value
}

function normalizeCount(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value))
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.trunc(parsed))
    }
  }
  return 0
}

function normalizeRecording(recording) {
  if (!recording || typeof recording !== 'object') {
    return recording
  }

  return {
    ...recording,
    recordingStartedAt: normalizeTimestamp(recording.recordingStartedAt),
    recordingStoppedAt: normalizeTimestamp(recording.recordingStoppedAt),
  }
}

function normalizeBookedEvent(bookedEvent) {
  if (!bookedEvent || typeof bookedEvent !== 'object') {
    return bookedEvent
  }

  return {
    ...bookedEvent,
    startTime: normalizeTimestamp(bookedEvent.startTime),
    endTime: normalizeTimestamp(bookedEvent.endTime),
  }
}

function normalizeSession(session) {
  if (!session || typeof session !== 'object') {
    return session
  }

  return {
    ...session,
    bookedEvent: normalizeBookedEvent(session.bookedEvent),
    latestRecording: normalizeRecording(session.latestRecording),
  }
}

function normalizeSessionsPage(page = {}) {
  const content = Array.isArray(page.content) ? page.content.map(normalizeSession) : []

  return {
    ...buildPageFallback(page.number || 0, page.size || 20),
    ...page,
    content,
    totalElements: normalizeCount(page.totalElements),
    totalPages: normalizeCount(page.totalPages),
    size: normalizeCount(page.size || 20),
    number: normalizeCount(page.number || 0),
  }
}

export async function listVcAuditSessions({
  page = 0,
  size = 20,
  clinicBrandId,
  clinicLocationId,
  eventType,
  sessionStatusIds,
  startTime,
  endTime,
} = {}) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token) {
    return {
      ok: false,
      message: 'Missing auth token.',
      sessionsPage: buildPageFallback(page, size),
      liveCount: 0,
    }
  }

  try {
    const response = await axios.get(`${supportApiURL}${VC_AUDIT_SESSIONS_API}`, {
      headers,
      params: {
        languageCode: mainLanguageCode,
        platform: platformWeb,
        countryCode,
        page,
        size,
        clinicBrandId: clinicBrandId || undefined,
        clinicLocationId: clinicLocationId || undefined,
        eventTypes: normalizeArrayParam(eventType),
        sessionStatusIds: normalizeArrayParam(sessionStatusIds),
        startTime: startTime || undefined,
        endTime: endTime || undefined,
      },
      paramsSerializer: {
        indexes: null,
      },
    })

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message: getMessageMapFromCommonResponse(response) || 'Unable to load VC audit sessions.',
        sessionsPage: buildPageFallback(page, size),
        liveCount: 0,
      }
    }

    const data = response?.data?.data || {}
    return {
      ok: true,
      sessionsPage: normalizeSessionsPage(data.sessions || buildPageFallback(page, size)),
      liveCount: normalizeCount(data.liveCount),
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit sessions:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load VC audit sessions.'),
      sessionsPage: buildPageFallback(page, size),
      liveCount: 0,
    }
  }
}

export async function getVcAuditSessionDetails(bookedEventId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId) {
    return {
      ok: false,
      message: 'Booked event id is required.',
      session: null,
    }
  }

  try {
    const response = await axios.get(`${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}`, {
      headers,
      params: {
        languageCode: mainLanguageCode,
        platform: platformWeb,
        countryCode,
      },
    })

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message: getMessageMapFromCommonResponse(response) || 'Unable to load session details.',
        session: null,
      }
    }

    return {
      ok: true,
      session: normalizeSession(response?.data?.data?.session || null),
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit session details:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load session details.'),
      session: null,
    }
  }
}

export async function getVcAuditViewerBootstrap(bookedEventId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId) {
    return {
      ok: false,
      message: 'Booked event id is required.',
      bootstrap: null,
    }
  }

  try {
    const response = await axios.get(
      `${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}/viewer-bootstrap`,
      {
        headers,
        params: {
          languageCode: mainLanguageCode,
          platform: platformWeb,
          countryCode,
        },
      },
    )

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message:
          getMessageMapFromCommonResponse(response) ||
          'Unable to load the live viewer.',
        bootstrap: null,
      }
    }

    return {
      ok: true,
      bootstrap: response?.data?.data
        ? {
            ...response.data.data,
            session: normalizeSession(response.data.data.session || null),
            bookedEvent: normalizeBookedEvent(response.data.data.bookedEvent || null),
          }
        : null,
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit viewer bootstrap:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load the live viewer.'),
      bootstrap: null,
    }
  }
}

export async function listVcAuditRecordings(bookedEventId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId) {
    return {
      ok: false,
      message: 'Booked event id is required.',
      session: null,
      recordings: [],
    }
  }

  try {
    const response = await axios.get(
      `${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}/recordings`,
      {
        headers,
        params: {
          languageCode: mainLanguageCode,
          platform: platformWeb,
          countryCode,
        },
      },
    )

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message:
          getMessageMapFromCommonResponse(response) ||
          'Unable to load session recordings.',
        session: null,
        recordings: [],
      }
    }

    const data = response?.data?.data || {}
    return {
      ok: true,
      session: normalizeSession(data.session || null),
      recordings: Array.isArray(data.recordings) ? data.recordings.map(normalizeRecording) : [],
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit recordings:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load session recordings.'),
      session: null,
      recordings: [],
    }
  }
}

export async function getVcAuditRecordingPlayback(bookedEventId, recordingId) {
  const { token, countryCode, mainLanguageCode, headers } = buildHeaders()

  if (!token || !bookedEventId || !recordingId) {
    return {
      ok: false,
      message: 'Booked event id and recording id are required.',
      session: null,
      recording: null,
      playback: null,
    }
  }

  try {
    const response = await axios.get(
      `${supportApiURL}${VC_AUDIT_SESSIONS_API}/${bookedEventId}/recordings/${recordingId}/playback`,
      {
        headers,
        params: {
          languageCode: mainLanguageCode,
          platform: platformWeb,
          countryCode,
        },
      },
    )

    if (!isCommonResponseSuccessfulV2(response)) {
      return {
        ok: false,
        message:
          getMessageMapFromCommonResponse(response) ||
          'Unable to load recording playback.',
        session: null,
        recording: null,
        playback: null,
      }
    }

    const data = response?.data?.data || {}
    return {
      ok: true,
      session: normalizeSession(data.session || null),
      recording: normalizeRecording(data.recording || null),
      playback: data.playback
        ? {
            ...data.playback,
            recordingStartedAt: normalizeTimestamp(data.playback.recordingStartedAt),
            recordingStoppedAt: normalizeTimestamp(data.playback.recordingStoppedAt),
          }
        : null,
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error loading VC audit recording playback:', error)
    return {
      ok: false,
      message: buildErrorMessage(error, 'Unable to load recording playback.'),
      session: null,
      recording: null,
      playback: null,
    }
  }
}
